import * as Blockly from 'blockly/core'
import { clipboard } from 'blockly/core'
import { STRINGS } from '@tm/shared'
import { trackEvent } from '../analytics/plausible.js'

export function registerContextMenu() {
  Blockly.ContextMenuRegistry.registry.register({
    id: 'tm-delete',
    scopeType: Blockly.ContextMenuRegistry.ScopeType.BLOCK,
    displayText: STRINGS.contextMenuDelete,
    preconditionFn: () => 'enabled',
    callback: (scope) => {
      scope.block?.dispose(true)
      trackEvent('block_deleted', { via: 'context_menu' })
    },
    weight: 100,
  })

  Blockly.ContextMenuRegistry.registry.register({
    id: 'tm-duplicate',
    scopeType: Blockly.ContextMenuRegistry.ScopeType.BLOCK,
    displayText: STRINGS.contextMenuDuplicate,
    preconditionFn: () => 'enabled',
    callback: (scope) => {
      const block = scope.block
      if (!block) return
      const copyData = clipboard.copy(block)
      if (!copyData) return
      const workspace = block.workspace
      const clone = clipboard.paste(workspace)
      if (clone) {
        const pos = block.getRelativeToSurfaceXY()
        clone.translate(pos.x + 30, pos.y + 30)
        clone.snapToGrid?.()
      }
      trackEvent('block_added', { via: 'duplicate' })
    },
    weight: 90,
  })

  Blockly.ContextMenuRegistry.registry.register({
    id: 'tm-move-up',
    scopeType: Blockly.ContextMenuRegistry.ScopeType.BLOCK,
    displayText: STRINGS.contextMenuUp,
    preconditionFn: (scope) =>
      scope.block?.nextConnection?.targetConnection ? 'enabled' : 'disabled',
    callback: (scope) => moveBlock(scope.block, 'up'),
    weight: 80,
  })

  Blockly.ContextMenuRegistry.registry.register({
    id: 'tm-move-down',
    scopeType: Blockly.ContextMenuRegistry.ScopeType.BLOCK,
    displayText: STRINGS.contextMenuDown,
    preconditionFn: (scope) =>
      scope.block?.previousConnection?.targetConnection ? 'enabled' : 'disabled',
    callback: (scope) => moveBlock(scope.block, 'down'),
    weight: 70,
  })

  Blockly.ContextMenuRegistry.registry.register({
    id: 'tm-view-code',
    scopeType: Blockly.ContextMenuRegistry.ScopeType.BLOCK,
    displayText: STRINGS.contextMenuViewCode,
    preconditionFn: () => 'enabled',
    callback: (scope) => {
      const workspace = scope.block?.workspace
      if (!workspace) return
      showCodeOverlay(workspace)
    },
    weight: 60,
  })

  // NEW: Jalankan kode dari workspace-level context menu.
  Blockly.ContextMenuRegistry.registry.register({
    id: 'tm-run-code',
    scopeType: Blockly.ContextMenuRegistry.ScopeType.WORKSPACE,
    displayText: STRINGS.contextMenuRunCode,
    preconditionFn: () => 'enabled',
    callback: () => {
      const event = new CustomEvent('play_requested', { bubbles: true })
      document.querySelector('.tm-build')?.dispatchEvent(event)
    },
    weight: 55,
  })
}

function showCodeOverlay(workspace) {
  import('../codegen/index.js')
    .then(({ generateProjectCode }) => {
      let code
      try {
        code = generateProjectCode(workspace)
      } catch (err) {
        code = `// ${STRINGS.codeOverlayInvalid}\n// ${err.message}`
      }
      const overlay = createCodeOverlay(code)
      document.body.appendChild(overlay)
    })
    .catch((err) => {
      const code = `// ${STRINGS.codeOverlayInvalid}\n// ${err.message}`
      document.body.appendChild(createCodeOverlay(code))
    })
}

function createCodeOverlay(code) {
  const overlay = document.createElement('div')
  overlay.className = 'tm-code-overlay'
  overlay.innerHTML = `
    <div class="tm-code-overlay__panel">
      <div class="tm-code-overlay__header">
        <h2 class="tm-code-overlay__title">${STRINGS.codeOverlayTitle}</h2>
        <button type="button" class="tm-code-overlay__close" aria-label="${STRINGS.codeOverlayClose}">✕</button>
      </div>
      <pre class="tm-code-overlay__code"></pre>
      <button type="button" class="tm-code-overlay__copy">${STRINGS.codeOverlayCopy}</button>
    </div>
  `
  const codeEl = overlay.querySelector('.tm-code-overlay__code')
  codeEl.textContent = code || STRINGS.codeOverlayEmpty

  overlay.querySelector('.tm-code-overlay__close').addEventListener('click', () => overlay.remove())
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove()
  })
  overlay.querySelector('.tm-code-overlay__copy').addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(code)
    } catch {
      // clipboard not available
    }
    overlay.remove()
  })

  return overlay
}

function moveBlock(block, direction) {
  if (!block) return
  const target = direction === 'up' ? block.nextConnection : block.previousConnection
  if (!target) return
  const other = target.targetConnection
  if (!other) return
  const otherBlock = other.getSourceBlock()
  if (direction === 'up') {
    otherBlock.unplug(false)
    target.connect(other)
  } else {
    block.unplug(false)
    other.connect(target)
  }
  trackEvent('block_moved', { direction })
}
